import React from 'react';
import PolicyPageLayout, {
  PolicySection, Para, BulletList, BulletItem, SubHead, InfoBox, WarnBox, ContactCard,
} from '../components/policy/PolicyPageLayout';

const toc = [
  { id: 'scope',             label: 'Scope & Application' },
  { id: 'permitted',         label: 'Permitted Uses' },
  { id: 'prohibited-email',  label: 'Prohibited: Email Activity' },
  { id: 'prohibited-storage',label: 'Prohibited: Storage & Files' },
  { id: 'prohibited-system', label: 'Prohibited: System & Network' },
  { id: 'sending-limits',    label: 'Email Sending Limits' },
  { id: 'disclosure',        label: 'Disclosure & Transparency' },
  { id: 'enforcement',       label: 'Enforcement & Consequences' },
  { id: 'reporting',         label: 'Reporting Abuse' },
  { id: 'amendments',        label: 'Amendments' },
  { id: 'contact',           label: 'Contact' },
];

const related = [
  { label: 'Terms of Service',  href: '/terms-of-service' },
  { label: 'Privacy Policy',    href: '/privacy-policy' },
  { label: 'Refund Policy',     href: '/refund-policy' },
];

const AcceptableUsePolicyPage: React.FC = () => (
  <PolicyPageLayout
    title="Acceptable Use Policy"
    lastUpdated="April 26, 2026"
    toc={toc}
    related={related}
  >

    <PolicySection id="scope" number="1" title="Scope and Application">
      <Para>
        This Acceptable Use Policy ("AUP") governs all use of the CruvzMail email hosting platform,
        webmail client, DriveX file storage, APIs, and related services (collectively, the "Service"),
        operated by Cruvz Online Shopping India Pvt Ltd. This AUP applies to all users — individual
        account holders, organizational administrators, and any third parties accessing the Service on
        your behalf.
      </Para>
      <Para>
        Violations of this AUP constitute a breach of the Terms of Service and may result in immediate
        account suspension or termination without refund.
      </Para>
    </PolicySection>

    <PolicySection id="permitted" number="2" title="Permitted Uses">
      <InfoBox>
        CruvzMail is designed for legitimate business and professional communication. These are the
        uses we actively support and optimize for.
      </InfoBox>
      <Para>You may use the Service to:</Para>
      <BulletList>
        <BulletItem>Send and receive transactional and professional email for legitimate business purposes</BulletItem>
        <BulletItem>Collaborate with colleagues, clients, and partners within your organization's domain</BulletItem>
        <BulletItem>Store and share business-related documents, files, and attachments via DriveX</BulletItem>
        <BulletItem>Manage contacts, calendar events, tasks, and notes for professional use</BulletItem>
        <BulletItem>Use CruvzMail APIs to integrate with your own applications and workflows</BulletItem>
        <BulletItem>Send opt-in newsletters or updates to recipients who have explicitly consented</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="prohibited-email" number="3" title="Prohibited: Email Activity">
      <WarnBox>
        Sending prohibited email is the most serious violation of this AUP. It damages our IP
        reputation and affects all users on the platform. We take email abuse extremely seriously.
      </WarnBox>
      <Para>You must not use the Service to:</Para>
      <BulletList>
        <BulletItem>
          <strong>Send spam:</strong> Transmit unsolicited bulk email (UBE) or unsolicited commercial
          email (UCE) to recipients who have not opted in
        </BulletItem>
        <BulletItem>
          <strong>Phishing and fraud:</strong> Send emails designed to deceive recipients into revealing
          sensitive information, credentials, or making fraudulent payments
        </BulletItem>
        <BulletItem>
          <strong>Impersonation:</strong> Forge email headers, impersonate another person or
          organization, or misrepresent the origin of any email
        </BulletItem>
        <BulletItem>
          <strong>Harvest and scrape:</strong> Collect email addresses by automated means without
          explicit consent from the collected individuals
        </BulletItem>
        <BulletItem>
          <strong>Malware distribution:</strong> Send emails containing viruses, ransomware, spyware,
          trojans, exploits, or other malicious code
        </BulletItem>
        <BulletItem>
          <strong>Harassment:</strong> Use email to threaten, bully, stalk, harass, or abuse any
          individual or group
        </BulletItem>
        <BulletItem>
          <strong>Illegal content:</strong> Distribute content that violates applicable law, including
          CSAM, illegal gambling, or unlicensed pharmaceuticals
        </BulletItem>
        <BulletItem>
          <strong>Circumventing recipient restrictions:</strong> Continue to email recipients who have
          unsubscribed, blocked, or explicitly requested no further contact
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="prohibited-storage" number="4" title="Prohibited: Storage and Files (DriveX)">
      <WarnBox>
        All files stored in DriveX are scanned by ClamAV. Malware is quarantined automatically.
        Accounts storing prohibited content are subject to immediate suspension.
      </WarnBox>
      <Para>You must not store or distribute via DriveX:</Para>
      <BulletList>
        <BulletItem>Child sexual abuse material (CSAM) — results in immediate termination and law enforcement referral</BulletItem>
        <BulletItem>Malware, ransomware, exploit kits, or any malicious executables</BulletItem>
        <BulletItem>Pirated software, movies, music, or other copyright-infringing content</BulletItem>
        <BulletItem>Content that facilitates illegal weapons manufacture, drug production, or trafficking</BulletItem>
        <BulletItem>Material that violates the privacy or intellectual property rights of others</BulletItem>
        <BulletItem>Content used for large-scale copyright infringement or unauthorized redistribution</BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="prohibited-system" number="5" title="Prohibited: System and Network Activity">
      <Para>You must not:</Para>
      <BulletList>
        <BulletItem>
          Probe, scan, or test the vulnerability of the Service or its infrastructure without explicit
          written authorization from CruvzMail
        </BulletItem>
        <BulletItem>
          Attempt to circumvent authentication, authorization, or encryption mechanisms
        </BulletItem>
        <BulletItem>
          Launch denial-of-service (DoS/DDoS) attacks, mail floods, or any action that degrades
          Service performance for other users
        </BulletItem>
        <BulletItem>
          Access, query, or export data belonging to other accounts without authorization
        </BulletItem>
        <BulletItem>
          Use automated tools to exceed published API rate limits or bypass throttling mechanisms
        </BulletItem>
        <BulletItem>
          Resell, sublicense, or provide access to the Service to third parties without our written
          consent
        </BulletItem>
        <BulletItem>
          Use the Service in any manner that violates applicable law, including export control laws
        </BulletItem>
      </BulletList>
    </PolicySection>

    <PolicySection id="sending-limits" number="6" title="Email Sending Limits">
      <Para>
        To protect deliverability for all users, the following per-account sending limits are enforced:
      </Para>
      <BulletList>
        <BulletItem><strong>Daily limit:</strong> 500 emails per account</BulletItem>
        <BulletItem><strong>Hourly limit:</strong> 100 emails per account</BulletItem>
        <BulletItem><strong>Recipients per email:</strong> Maximum 100 recipients (To + CC + BCC combined)</BulletItem>
        <BulletItem><strong>Attachment limit:</strong> 25 MB per email, maximum 10 files</BulletItem>
      </BulletList>
      <SubHead>Velocity Detection</SubHead>
      <Para>
        Our systems automatically detect unusual sending patterns — such as sending to many new
        recipients rapidly or high bounce rates — and may temporarily throttle or suspend sending
        while we investigate. You will be notified by email if your account is throttled.
      </Para>
      <Para>
        Organizations needing higher limits may contact sales@cruvzmail.com to discuss Enterprise plan
        options.
      </Para>
    </PolicySection>

    <PolicySection id="disclosure" number="7" title="Disclosure and Transparency">
      <Para>
        We may disclose account information and usage data to law enforcement authorities, regulatory
        bodies, or other parties when required by applicable law, court order, or legal process. We
        will notify affected users of such disclosures where legally permissible.
      </Para>
      <Para>
        We cooperate with abuse reports from internet service providers (ISPs), email providers, and
        anti-spam organizations. If your domain or IP appears on established email blocklists
        (Spamhaus, SURBL, etc.) due to your sending activity, your sending access may be suspended
        pending investigation.
      </Para>
    </PolicySection>

    <PolicySection id="enforcement" number="8" title="Enforcement and Consequences">
      <Para>
        We reserve the right to investigate suspected violations and take appropriate action, including:
      </Para>
      <BulletList>
        <BulletItem>Issuing a formal warning and requiring corrective action</BulletItem>
        <BulletItem>Temporary suspension of email sending or file upload capabilities</BulletItem>
        <BulletItem>Permanent suspension or termination of the account without refund</BulletItem>
        <BulletItem>Referral to law enforcement for criminal activity including CSAM and fraud</BulletItem>
        <BulletItem>Civil legal action to recover damages caused by AUP violations</BulletItem>
      </BulletList>
      <Para>
        The severity of enforcement is proportional to the nature and severity of the violation.
        We aim to give advance warning for less severe violations, but immediate termination without
        notice may occur for spam campaigns, malware distribution, or CSAM.
      </Para>
    </PolicySection>

    <PolicySection id="reporting" number="9" title="Reporting Abuse">
      <Para>
        If you encounter abuse originating from a CruvzMail account — including spam, phishing, or
        harassment — please report it promptly. We investigate all credible abuse reports.
      </Para>
      <ContactCard>
        <strong>Email abuse:</strong> abuse@cruvzmail.com<br />
        <strong>Security vulnerabilities:</strong> security@cruvzmail.com<br />
        <strong>CSAM reports:</strong> Immediately contact NCMEC at cybertipline.org and law enforcement
      </ContactCard>
      <Para>
        Please include the full email headers (not just the subject line), the date and time received,
        and any relevant context to help us investigate effectively.
      </Para>
    </PolicySection>

    <PolicySection id="amendments" number="10" title="Amendments">
      <Para>
        We may update this AUP from time to time to address new threats, technologies, or regulatory
        requirements. We will notify affected users of material changes via email or in-platform
        notice. Continued use of the Service after the effective date of changes constitutes acceptance
        of the revised AUP.
      </Para>
    </PolicySection>

    <PolicySection id="contact" number="11" title="Contact Us">
      <Para>For questions about this Acceptable Use Policy:</Para>
      <ContactCard>
        <strong>Abuse reports:</strong> abuse@cruvzmail.com<br />
        <strong>Legal &amp; compliance:</strong> legal@cruvzmail.com<br />
        <strong>General support:</strong> support@cruvzmail.com<br />
        <br />
        <strong>CruvzMail</strong> — operated by Cruvz Online Shopping India Pvt Ltd<br />
        cruvzmail.com
      </ContactCard>
    </PolicySection>

  </PolicyPageLayout>
);

export default AcceptableUsePolicyPage;
